/**
 * SEOEngine.jsx
 * AI Growth Engine — Semi-automatic SEO approval workflow
 * Flow: Scan → Generate AI → Merchant Reviews → Approve → Apply to Salla
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Zap, CheckCircle2, Loader2, Pencil, Check, X,
  AlertTriangle, Sparkles, TrendingUp, ArrowRight, ChevronDown,
  ChevronUp, Info, BarChart2, FileText, Shield, Eye
} from "lucide-react";
import SEODashboard from "@/components/SEODashboard";
import ImageAltManager from "@/components/ImageAltManager";

/* ─────────────────────────── tiny helpers ─────────────────────────── */

function wordCount(text) {
  return text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
}

function charCount(text) {
  return text ? text.trim().length : 0;
}

/* ─────────────────────────── sub-components ───────────────────────── */

/** Animated SVG score ring */
function ScoreRing({ score, size = 76 }) {
  const r = 26;
  const circ = 2 * Math.PI * r;
  const fill = score == null ? 0 : (score / 100) * circ;
  const color =
    score == null ? "#94a3b8" : score >= 70 ? "#22c55e" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
      <circle
        cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 32 32)"
        style={{ transition: "stroke-dasharray 0.9s ease" }}
      />
      <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="800" fill={color}>
        {score == null ? "—" : score}
      </text>
    </svg>
  );
}

/** Impact badge: shows estimated SEO improvement % */
function ImpactBadge({ score }) {
  if (!score) return null;
  const color =
    score >= 70 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    score >= 45 ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-blue-50 text-blue-700 border-blue-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${color}`}>
      <TrendingUp className="w-2.5 h-2.5" />
      +{score}% تحسين
    </span>
  );
}

/** Severity badge */
function SeverityBadge({ severity }) {
  const map = {
    high: "bg-red-100 text-red-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-blue-100 text-blue-700",
  };
  const label = { high: "عالية", medium: "متوسطة", low: "منخفضة" };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[severity] || map.low}`}>
      {label[severity] || severity}
    </span>
  );
}

/** Status badge */
function StatusBadge({ status }) {
  const map = {
    pending: "bg-slate-100 text-slate-600",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-600",
    applied: "bg-blue-100 text-blue-700",
  };
  const label = {
    pending: "بانتظار المراجعة",
    approved: "معتمد",
    rejected: "مرفوض",
    applied: "مُطبَّق",
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[status] || ""}`}>
      {label[status] || status}
    </span>
  );
}

/** Tone selector pills */
const TONES = [
  { id: "gulf",         label: "خليجي",       desc: "ودّي ومباشر" },
  { id: "professional", label: "احترافي",     desc: "متوازن وموضوعي" },
  { id: "sporty",       label: "رياضي",       desc: "حيوي ونشيط" },
  { id: "luxury",       label: "فاخر",        desc: "راقٍ وأنيق" },
  { id: "minimal",      label: "مختصر",       desc: "جمل قصيرة" },
];

function ToneSelector({ value, onChange }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
      <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mb-2">
        نبرة المحتوى المُولَّد
      </p>
      <div className="flex flex-wrap gap-2">
        {TONES.map((t) => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            data-testid={`tone-${t.id}`}
            className={`flex flex-col items-start px-3 py-2 rounded-xl border text-right transition-all ${
              value === t.id
                ? "bg-[#004EEB] border-[#004EEB] text-white shadow-sm"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <span className="text-xs font-bold leading-none">{t.label}</span>
            <span className={`text-[10px] mt-0.5 ${value === t.id ? "text-blue-100" : "text-slate-400"}`}>
              {t.desc}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
function WorkflowSteps({ currentStep }) {
  const steps = [
    { id: 1, label: "فحص المتجر", icon: <Search className="w-3.5 h-3.5" /> },
    { id: 2, label: "توليد AI", icon: <Sparkles className="w-3.5 h-3.5" /> },
    { id: 3, label: "مراجعة وموافقة", icon: <Eye className="w-3.5 h-3.5" /> },
    { id: 4, label: "تطبيق على سلة", icon: <Zap className="w-3.5 h-3.5" /> },
  ];
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1 no-scrollbar">
      {steps.map((step, i) => (
        <div key={step.id} className="flex items-center shrink-0">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            currentStep === step.id
              ? "bg-[#004EEB] text-white shadow-sm"
              : currentStep > step.id
              ? "bg-green-100 text-green-700"
              : "bg-slate-100 text-slate-400"
          }`}>
            {currentStep > step.id ? <CheckCircle2 className="w-3 h-3" /> : step.icon}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="w-3 h-3 text-slate-300 mx-1 shrink-0" />
          )}
        </div>
      ))}
    </div>
  );
}

/** Before / After comparison panel */
function ComparePanel({ sug, editing, onEditChange }) {
  const isTitle = sug.type === "title";
  const origText = sug.original || "";

  const origStat = isTitle
    ? `${charCount(origText)} حرف`
    : `${wordCount(origText)} كلمة`;
  const suggStat = isTitle
    ? `${charCount(sug.suggested)} حرف`
    : `${wordCount(sug.suggested)} كلمة`;

  /* Render description with bullet + bold support */
  function renderFormatted(text) {
    if (!text) return <span className="italic text-slate-400">لا يوجد</span>;

    // Parse inline **bold** within a text segment
    function parseBold(str) {
      const parts = str.split(/\*\*(.+?)\*\*/g);
      return parts.map((part, i) =>
        i % 2 === 1
          ? <strong key={i} className="font-bold text-slate-800">{part}</strong>
          : <span key={i}>{part}</span>
      );
    }

    const lines = text.split("\n");
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-2" />;
      const isBullet = trimmed.startsWith("•") || trimmed.startsWith("-") || trimmed.startsWith("*");
      const content = isBullet ? trimmed.replace(/^[•\-*]\s*/, "") : trimmed;
      return (
        <div key={i} className={`flex gap-1.5 ${isBullet ? "items-start" : ""}`}>
          {isBullet && <span className="text-emerald-500 mt-0.5 shrink-0 font-bold">•</span>}
          <span>{parseBold(content)}</span>
        </div>
      );
    });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
      {/* Before */}
      <div className="bg-red-50 border border-red-100 rounded-xl p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold text-red-500 uppercase tracking-wide">قبل</span>
          <span className="text-[10px] text-red-400 bg-red-100 px-1.5 py-0.5 rounded-full">{origStat}</span>
        </div>
        <div className="text-xs text-slate-500 leading-relaxed space-y-0.5">
          {origText
            ? <p className={isTitle ? "" : "line-clamp-4"}>{origText}</p>
            : <span className="italic">لا يوجد</span>}
        </div>
      </div>

      {/* After */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">مقترح AI</span>
          <span className="text-[10px] text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">{suggStat}</span>
        </div>
        {editing !== undefined ? (
          <textarea
            value={editing}
            onChange={(e) => onEditChange(e.target.value)}
            data-testid={`edit-textarea-${sug.id}`}
            className="w-full text-xs text-slate-700 bg-white border border-emerald-300 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400"
            rows={isTitle ? 2 : 8}
          />
        ) : (
          <div className="text-xs text-slate-700 leading-relaxed space-y-0.5">
            {renderFormatted(sug.suggested)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Impact details expandable */
function ImpactDetails({ sug }) {
  const [open, setOpen] = useState(false);
  if (!sug.impact_score) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
      >
        <Info className="w-3 h-3" />
        تفاصيل التأثير المتوقع
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {(sug.impact_factors || []).map((f, i) => (
            <li key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600">
              <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────────── main component ───────────────────────── */

export default function SEOEngine({ sallaConnected }) {
  const [subTab, setSubTab]           = useState("dashboard");
  const [audit, setAudit]             = useState(null);
  const [polling, setPolling]         = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sugFilter, setSugFilter]     = useState("pending");
  const [sugLoading, setSugLoading]   = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [applying, setApplying]       = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Tone for generation
  const [tone, setTone] = useState("gulf");

  // Audit tab selection (products)
  const [auditSelected, setAuditSelected] = useState(new Set());
  const [expanded, setExpanded]           = useState({});

  // Suggestions tab selection (bulk)
  const [bulkSelected, setBulkSelected] = useState(new Set());

  const [editing, setEditing]   = useState({});   // suggestion_id → text
  const [score, setScore]       = useState(null);

  /* ── derive workflow step ── */
  const hasAudit   = audit?.status === "done";
  const hasPending = suggestions.some((s) => s.status === "pending");
  const hasApproved = suggestions.some((s) => s.status === "approved");
  const currentStep = applying ? 4 : hasApproved ? 3 : hasPending ? 3 : hasAudit ? 2 : 1;

  useEffect(() => {
    fetchScore();
    fetchAudit();
    fetchSuggestions(sugFilter);
  }, []); // eslint-disable-line

  const fetchScore = async () => {
    try {
      const { data } = await api.get("/seo/score");
      setScore(data);
    } catch {}
  };

  const fetchAudit = async () => {
    try {
      const { data } = await api.get("/seo/audit/latest");
      setAudit(data);
    } catch {}
  };

  const fetchSuggestions = async (statusFilter = sugFilter) => {
    setSugLoading(true);
    try {
      const { data } = await api.get(`/seo/suggestions?status=${statusFilter}`);
      setSuggestions(data.suggestions || []);
    } catch {}
    finally { setSugLoading(false); }
  };

  /* Poll while audit is running */
  useEffect(() => {
    if (audit?.status !== "running") { setPolling(false); return; }
    setPolling(true);
    const id = setInterval(async () => {
      const { data } = await api.get("/seo/audit/latest").catch(() => ({ data: null }));
      if (data) { setAudit(data); fetchScore(); }
      if (data?.status !== "running") { clearInterval(id); setPolling(false); }
    }, 3000);
    return () => clearInterval(id);
  }, [audit?.status]);

  const startAudit = async () => {
    if (!sallaConnected) { toast.error("يلزم ربط متجر سلة أولاً"); return; }
    try {
      const { data } = await api.post("/seo/audit");
      setAudit({ ...audit, status: "running", id: data.audit_id });
      toast.info("جاري فحص المتجر...");
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
  };

  const generateForSelected = async () => {
    if (auditSelected.size === 0) { toast.error("حدد منتجاً واحداً على الأقل"); return; }
    if (!sallaConnected) { toast.error("يلزم ربط متجر سلة أولاً"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/seo/generate", {
        product_ids: [...auditSelected],
        types: ["title", "description"],
        tone,
      });
      toast.success(`تم توليد ${data.created} اقتراح — راجعها للموافقة`);
      setAuditSelected(new Set());
      setSubTab("suggestions");
      fetchSuggestions("pending");
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ في التوليد"); }
    finally { setGenerating(false); }
  };

  /* Individual actions */
  const approveSuggestion = async (id) => {
    try {
      await api.put(`/seo/suggestions/${id}/approve`);
      setSuggestions((s) => s.map((x) => (x.id === id ? { ...x, status: "approved" } : x)));
      toast.success("تمت الموافقة");
    } catch { toast.error("خطأ"); }
  };

  const rejectSuggestion = async (id) => {
    try {
      await api.put(`/seo/suggestions/${id}/reject`);
      setSuggestions((s) => s.map((x) => (x.id === id ? { ...x, status: "rejected" } : x)));
    } catch { toast.error("خطأ"); }
  };

  const saveEdit = async (id) => {
    const text = editing[id];
    if (!text?.trim()) return;
    try {
      await api.post(`/seo/suggestions/${id}/edit`, { suggested: text });
      setSuggestions((s) => s.map((x) => (x.id === id ? { ...x, suggested: text } : x)));
      setEditing((e) => { const n = { ...e }; delete n[id]; return n; });
      toast.success("تم التعديل");
    } catch { toast.error("خطأ"); }
  };

  /* Bulk actions */
  const toggleBulkSelect = (id) => {
    setBulkSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAllPending = () => {
    const ids = suggestions.filter((s) => s.status === "pending").map((s) => s.id);
    setBulkSelected(new Set(ids));
  };

  const clearBulkSelect = () => setBulkSelected(new Set());

  const bulkAction = async (action) => {
    const ids = [...bulkSelected];
    if (ids.length === 0) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/seo/bulk-action", { suggestion_ids: ids, action });
      const label = action === "approve" ? "معتمد" : "مرفوض";
      toast.success(`تم تحديث ${data.updated} اقتراح — ${label}`);
      setBulkSelected(new Set());
      fetchSuggestions(sugFilter);
      fetchScore();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
    finally { setBulkLoading(false); }
  };

  const applyApproved = async () => {
    const approvedIds = suggestions.filter((s) => s.status === "approved").map((s) => s.id);
    if (approvedIds.length === 0) { toast.error("لا توجد اقتراحات معتمدة"); return; }
    setApplying(true);
    try {
      await api.post("/seo/apply", { suggestion_ids: approvedIds });
      toast.success("جاري التطبيق على سلة...");
      setTimeout(() => { fetchSuggestions(sugFilter); fetchScore(); }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
    finally { setApplying(false); }
  };

  const productsWithIssues = (audit?.products || []).filter((p) => p.has_issues);
  const pendingSuggestions = suggestions.filter((s) => s.status === "pending");
  const approvedCount      = suggestions.filter((s) => s.status === "approved").length;

  return (
    <div className="space-y-5">

      {/* ── Workflow Steps ── */}
      <div className="bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm">
        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-2">سير عمل التحسين</p>
        <WorkflowSteps currentStep={currentStep} />
      </div>

      {/* ── Score Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <ScoreRing score={score?.score ?? null} />
          <div>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">نقاط SEO</p>
            <p className="text-2xl font-black text-slate-800">
              {score?.score ?? "—"}
              <span className="text-sm font-normal text-slate-400">/100</span>
            </p>
            {score?.has_audit && (
              <p className="text-[10px] text-slate-400">{score.total_products} منتج مفحوص</p>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">مشاكل SEO</p>
            <p className="text-2xl font-black text-slate-800">{audit?.products_with_issues ?? "—"}</p>
            <p className="text-[10px] text-slate-400">منتج يحتاج تحسين</p>
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">اقتراحات AI</p>
            <p className="text-2xl font-black text-slate-800">{pendingSuggestions.length}</p>
            <p className="text-[10px] text-slate-400">بانتظار مراجعتك</p>
          </div>
        </div>
      </div>

      {/* ── Sub Tabs ── */}
      <div className="flex gap-1 border-b border-slate-100">
        {[
          { id: "dashboard",   label: "لوحة SEO",          icon: <BarChart2 className="w-3.5 h-3.5" /> },
          { id: "audit",       label: "فحص SEO",            icon: <Search className="w-3.5 h-3.5" /> },
          { id: "suggestions", label: "مراجعة الاقتراحات",  icon: <Shield className="w-3.5 h-3.5" />, badge: pendingSuggestions.length },
          { id: "images",      label: "صور المنتجات",       icon: <Eye className="w-3.5 h-3.5" /> },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            data-testid={`seo-tab-${t.id}`}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              subTab === t.id
                ? "border-[#004EEB] text-[#004EEB]"
                : "border-transparent text-slate-400 hover:text-slate-700"
            }`}
          >
            {t.icon}{t.label}
            {t.badge > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#004EEB] text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {t.badge > 9 ? "9+" : t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════ DASHBOARD TAB ══════════════ */}
      {subTab === "dashboard" && (
        <SEODashboard onGoToAudit={() => setSubTab("audit")} />
      )}

      {/* ══════════════ IMAGES TAB ══════════════ */}
      {subTab === "images" && (
        <ImageAltManager sallaConnected={sallaConnected} />
      )}

      {/* ══════════════ AUDIT TAB ══════════════ */}
      {subTab === "audit" && (
        <div className="space-y-4">
          {!sallaConnected && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              يلزم ربط متجر سلة لبدء الفحص. اذهب للإعدادات لربط متجرك.
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={startAudit}
              disabled={polling || !sallaConnected}
              data-testid="start-audit-btn"
              className="flex items-center gap-2 bg-[#004EEB] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {polling ? "جاري الفحص..." : "ابدأ فحص SEO"}
            </button>

            {hasAudit && auditSelected.size === 0 && productsWithIssues.length > 0 && (
              <button
                onClick={() => setAuditSelected(new Set(productsWithIssues.map((p) => p.product_id)))}
                className="text-sm text-[#004EEB] font-semibold hover:underline"
              >
                تحديد الكل ({productsWithIssues.length})
              </button>
            )}

            {auditSelected.size > 0 && (
              <>
                <ToneSelector value={tone} onChange={setTone} />
                <button
                  onClick={generateForSelected}
                  disabled={generating || !sallaConnected}
                  data-testid="generate-seo-btn"
                  className="flex items-center gap-2 bg-amber-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generating ? "جاري التوليد..." : `توليد AI لـ ${auditSelected.size} منتج`}
                </button>
              </>
            )}
          </div>

          {polling && (
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-xl px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              جاري فحص المنتجات، قد يستغرق دقيقة...
            </div>
          )}

          {hasAudit && (
            productsWithIssues.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
                <p className="text-slate-500 font-medium">رائع! لا توجد مشاكل SEO في متجرك</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-semibold">
                  {productsWithIssues.length} منتج يحتاج تحسين — حدد المنتجات وولّد اقتراحات AI
                </p>
                {productsWithIssues.map((p) => (
                  <div
                    key={p.product_id}
                    className={`border rounded-2xl overflow-hidden transition-all ${
                      auditSelected.has(p.product_id)
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-100 bg-white"
                    }`}
                  >
                    <div
                      className="flex items-center gap-3 p-3 cursor-pointer"
                      onClick={() => setExpanded((e) => ({ ...e, [p.product_id]: !e[p.product_id] }))}
                    >
                      <input
                        type="checkbox"
                        checked={auditSelected.has(p.product_id)}
                        data-testid={`select-product-${p.product_id}`}
                        onChange={() =>
                          setAuditSelected((s) => {
                            const n = new Set(s);
                            n.has(p.product_id) ? n.delete(p.product_id) : n.add(p.product_id);
                            return n;
                          })
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-blue-600 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{p.product_name}</p>
                        <p className="text-xs text-slate-400">{p.issues.length} مشكلة</p>
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {p.issues.slice(0, 2).map((iss, i) => (
                          <SeverityBadge key={i} severity={iss.severity} />
                        ))}
                      </div>
                      {expanded[p.product_id]
                        ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                      }
                    </div>
                    {expanded[p.product_id] && (
                      <div className="border-t border-slate-100 px-4 py-3 space-y-2">
                        {p.issues.map((iss, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <span className="text-xs text-slate-600">{iss.label}</span>
                            <SeverityBadge severity={iss.severity} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {(!audit || audit.status === "none") && !polling && (
            <div className="text-center py-14 text-slate-400">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-25" />
              <p className="text-sm">اضغط "ابدأ فحص SEO" لتحليل جميع منتجات متجرك</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════ SUGGESTIONS TAB ══════════════ */}
      {subTab === "suggestions" && (
        <div className="space-y-4">

          {/* ── Bulk selection bar ── */}
          {bulkSelected.size > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="flex items-center gap-2 bg-[#004EEB] text-white rounded-xl px-4 py-2.5 flex-wrap"
            >
              <span className="text-sm font-semibold">
                {bulkSelected.size} اقتراح محدد
              </span>
              <div className="flex gap-2 mr-auto flex-wrap">
                <button
                  onClick={() => bulkAction("approve")}
                  disabled={bulkLoading}
                  data-testid="bulk-approve-btn"
                  className="flex items-center gap-1.5 bg-white text-green-700 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-50 disabled:opacity-50"
                >
                  {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  موافقة جماعية
                </button>
                <button
                  onClick={() => bulkAction("reject")}
                  disabled={bulkLoading}
                  data-testid="bulk-reject-btn"
                  className="flex items-center gap-1.5 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[var(--bg-card-hover)]/30 disabled:opacity-50"
                >
                  <X className="w-3 h-3" /> رفض جماعي
                </button>
                <button
                  onClick={clearBulkSelect}
                  className="text-white/70 text-xs hover:text-white px-2"
                >
                  إلغاء
                </button>
              </div>
            </div>
          )}

          {/* ── Filter + Apply bar ── */}
          <div className="flex items-center gap-2 flex-wrap">
            {["pending", "approved", "applied", "rejected", "all"].map((f) => (
              <button
                key={f}
                onClick={() => { setSugFilter(f); fetchSuggestions(f); setBulkSelected(new Set()); }}
                data-testid={`sug-filter-${f}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  sugFilter === f
                    ? "bg-[#004EEB] text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {{ pending: "بانتظار المراجعة", approved: "معتمد", applied: "مُطبَّق", rejected: "مرفوض", all: "الكل" }[f]}
                {f === "pending" && pendingSuggestions.length > 0 && (
                  <span className="mr-1 bg-white/30 text-[9px] px-1 rounded-full">{pendingSuggestions.length}</span>
                )}
              </button>
            ))}

            <div className="mr-auto flex gap-2">
              {sugFilter === "pending" && pendingSuggestions.length > 0 && bulkSelected.size === 0 && (
                <button
                  onClick={selectAllPending}
                  data-testid="select-all-pending-btn"
                  className="text-xs text-[#004EEB] font-semibold hover:underline"
                >
                  تحديد الكل ({pendingSuggestions.length})
                </button>
              )}
              {approvedCount > 0 && (
                <button
                  onClick={applyApproved}
                  disabled={applying}
                  data-testid="apply-to-salla-btn"
                  className="flex items-center gap-2 bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-green-700 disabled:opacity-50"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  {applying ? "جاري التطبيق..." : `تطبيق على سلة (${approvedCount})`}
                </button>
              )}
            </div>
          </div>

          {/* ── List ── */}
          {sugLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-14 text-slate-400">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-25" />
              <p className="text-sm font-medium">لا توجد اقتراحات بعد</p>
              <p className="text-xs mt-1 text-slate-400">افحص المتجر أولاً، ثم حدد المنتجات وولّد اقتراحات AI</p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((sug) => {
                const isBulkChecked = bulkSelected.has(sug.id);
                const isEditing     = editing[sug.id] !== undefined;
                return (
                  <div
                    key={sug.id}
                    data-testid={`suggestion-card-${sug.id}`}
                    className={`bg-white border rounded-2xl overflow-hidden shadow-sm transition-all ${
                      isBulkChecked ? "border-blue-300 ring-1 ring-blue-200" : "border-slate-100"
                    }`}
                  >
                    {/* Card header */}
                    <div className="flex items-start gap-3 p-4 pb-2">
                      {/* Bulk checkbox — only for pending */}
                      {sug.status === "pending" && (
                        <input
                          type="checkbox"
                          checked={isBulkChecked}
                          data-testid={`bulk-checkbox-${sug.id}`}
                          onChange={() => toggleBulkSelect(sug.id)}
                          className="w-4 h-4 accent-blue-600 mt-0.5 shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                            {sug.type === "title" ? "عنوان" : "وصف"}
                          </span>
                          <StatusBadge status={sug.status} />
                          {sug.impact_score && <ImpactBadge score={sug.impact_score} />}
                          {sug.tone && sug.tone !== "gulf" && (
                            <span className="text-[10px] font-semibold bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
                              {TONES.find(t => t.id === sug.tone)?.label || sug.tone}
                            </span>
                          )}
                          <span className="text-xs text-slate-400 truncate max-w-[200px]">{sug.product_name}</span>
                        </div>

                        {/* Before / After */}
                        <ComparePanel
                          sug={sug}
                          editing={isEditing ? editing[sug.id] : undefined}
                          onEditChange={(val) => setEditing((e) => ({ ...e, [sug.id]: val }))}
                        />

                        {/* Impact details */}
                        <ImpactDetails sug={sug} />
                      </div>
                    </div>

                    {/* Actions footer */}
                    {sug.status === "pending" && (
                      <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => approveSuggestion(sug.id)}
                          data-testid={`approve-${sug.id}`}
                          className="flex items-center gap-1 bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Check className="w-3 h-3" /> قبول
                        </button>
                        <button
                          onClick={() => rejectSuggestion(sug.id)}
                          data-testid={`reject-${sug.id}`}
                          className="flex items-center gap-1 bg-red-50 text-red-600 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <X className="w-3 h-3" /> رفض
                        </button>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(sug.id)}
                              data-testid={`save-edit-${sug.id}`}
                              className="flex items-center gap-1 bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Check className="w-3 h-3" /> حفظ التعديل
                            </button>
                            <button
                              onClick={() => setEditing((e) => { const n = { ...e }; delete n[sug.id]; return n; })}
                              className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5"
                            >
                              إلغاء
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setEditing((e) => ({ ...e, [sug.id]: sug.suggested }))}
                            data-testid={`edit-${sug.id}`}
                            className="flex items-center gap-1 text-slate-400 text-xs hover:text-slate-700 px-2 py-1.5 rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors"
                          >
                            <Pencil className="w-3 h-3" /> تعديل
                          </button>
                        )}
                      </div>
                    )}

                    {sug.status === "approved" && (
                      <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-1.5 text-xs text-green-600 font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        معتمد — جاهز للتطبيق على سلة
                      </div>
                    )}
                    {sug.status === "applied" && (
                      <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-1.5 text-xs text-blue-600 font-semibold">
                        <Zap className="w-3.5 h-3.5" />
                        تم التطبيق على سلة
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
